import { DateService } from '../services/date.service';
import { IDepartmentSelection } from '../interfaces/department/department-selection.interface';
import { IDepartment } from '../interfaces/department/department.interface';
import { IEmployee } from '../interfaces/IEmployee';

export class DepartmentOperations {
  public static readonly getName = (department: IDepartmentSelection): string => department.name;
  public static readonly isSameDepartment = (
    first: IDepartmentSelection | null,
    second: IDepartmentSelection | null,
  ): boolean => first?.id === second?.id;

  public static getSelectable(
    departments: IDepartment[],
    selectedDepartmentId: number | undefined,
    today: Date = new Date(),
  ): IDepartment[] {
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);

    return departments
      .filter(department => department.id === selectedDepartmentId || new Date(department.inactiveSince) > startOfToday)
      .sort((first, second) => first.name.localeCompare(second.name));
  }

  public static fromEmployee(employee?: IEmployee): IDepartmentSelection | null {
    return employee?.departmentId && employee.departmentName
      ? { id: employee.departmentId, name: employee.departmentName }
      : null;
  }

  public static assignToEmployee(employee: IEmployee, department: IDepartmentSelection | null): IEmployee {
    return {
      ...employee,
      departmentId: department?.id ?? null,
      departmentName: department?.name ?? null,
    };
  }

  public static forEditing(departments: IDepartment[], newDepartment: IDepartment | null): IDepartment[] {
    const persistedDepartments = departments.map(department => ({ ...department, disabled: true }));
    return newDepartment ? [newDepartment, ...persistedDepartments] : persistedDepartments;
  }

  public static createNew(): IDepartment {
    const inactiveSince = new Date();
    inactiveSince.setFullYear(inactiveSince.getFullYear() + 5);
    return { id: 0, name: '', inactiveSince, disabled: true };
  }

  public static toRequest(department: IDepartment, dateService: DateService): IDepartment {
    return {
      ...department,
      name: department.name.trim(),
      inactiveSince: dateService.toDateWithoutTimeZone(new Date(department.inactiveSince)),
    };
  }

  public static add(departments: IDepartment[], department: IDepartment): IDepartment[] {
    return [...departments, department].sort((first, second) => first.name.localeCompare(second.name));
  }

  public static replace(departments: IDepartment[], department: IDepartment): IDepartment[] {
    return departments
      .map(existing => (existing.id === department.id ? department : existing))
      .sort((first, second) => first.name.localeCompare(second.name));
  }
}
